import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DatacapResponse } from 'src/app/shared/models/DTO/FocusPayDatacap/DatacapAuthResponse.dto';
import { CheckSources } from 'src/app/shared/models/enums/check-sources';
import { KioskConfiguration } from 'src/app/shared/models/kiosk/kiosk.model';
import { OOCanvas } from 'src/app/shared/models/oo-canvas';
import { Order, OrderItem, OrderPayment, OrderSeat } from 'src/app/shared/models/order';
import { OrderPaymentTypes } from 'src/app/shared/models/order-payment-types.enum';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from './app.service';
import { DeviceService } from './device.service';
import { FocuslinkService } from './focuslink.service';
import { LocalDataStoreService } from './local-data-store.service';
import { OrderService } from './order.service';
import { OOVenueService } from './venue-service';

@Injectable({
	providedIn: 'root',
})
export class KioskService {
	activeOrder: BehaviorSubject<Order>;
	kioskConfiguration: KioskConfiguration;
	kioskMenu: OOCanvas[];

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		public venueService: OOVenueService,
		public orderService: OrderService,
		public deviceService: DeviceService,
		private localDataService: LocalDataStoreService
	) {}

	async buildKioskMenu() {
		await this.venueService.canvasesGet();
		await this.venueService.menuItemsGet();

		this.kioskMenu = this.venueService.ooCanvases.filter((canvas) => canvas.menuIds.includes(this.kioskConfiguration.menuKey));
		this.kioskMenu.forEach((canvas) => {
			canvas.menuItems.forEach((mi) => {
				mi.ooMenuItem = this.venueService.ooMenuItems.find((item) => item.menuItemKey === mi.menuItemKey);
				if (mi.ooMenuItem) {
					mi.ooMenuItem.canvases.forEach((cmi) => {
						const trueCanvas = this.venueService.ooCanvases.find((can) => can.canvasKey === cmi.canvasKey);
						trueCanvas && Object.assign(cmi, trueCanvas);
					});

					mi.includedItemCanvases = mi.ooMenuItem.canvases.filter((can) => can.includedItemCanvas);
					mi.modifierCanvases = mi.ooMenuItem.canvases.filter((can) => !can.includedItemCanvas && can.online);

					mi.includedItemCanvases.forEach((incMod) => {
						incMod.menuItems?.forEach((modItem) => {
							modItem.ooMenuItem = this.venueService.ooMenuItems.find((itm) => itm.menuItemKey === modItem.menuItemKey);
						});
					});

					mi.modifierCanvases.forEach((mod) => {
						mod.menuItems.forEach((modItem) => {
							modItem.ooMenuItem = this.venueService.ooMenuItems.find((itm) => itm.menuItemKey === modItem.menuItemKey);
						});
					});

					if (mi.menuItemKey === 416) {
						console.log(mi);
					}
				}
			});
		});
	}

	async deviceServiceSubscription() {
		const licenseKey = await this.localDataService.get('kioskLicenseKey');
		const licenseSecret = await this.localDataService.get('kioskLicenseSecret');
		const verifyLicenseResult = await this.deviceService.verifyLicense(licenseKey, licenseSecret);
		if (verifyLicenseResult) {
			console.log('Connecting to Device Socket');
			this.deviceService.connect(this.venueService.venueKey);
		} else {
			console.log('License result invalid');
		}
	}

	async getKioskConfiguration(kioskKey: string) {
		const resp = await this.focuslink.kioskGetConfiguration(this.venueService.venueKey, kioskKey);
		this.kioskConfiguration = new KioskConfiguration(resp);
	}

	initKioskVenue(kioskKey: string) {
		if (!this.venueService.venueKey) {
			return;
		}
		this.deviceServiceSubscription();
		this.venueService.venueSocketConnect(this.venueService.venueKey);
		this.getKioskConfiguration(kioskKey);
		this.venueService.ooVenueGet();
		this.venueService.ooStylesGet();
		this.venueService.ooOrderTypeGet();
		this.buildKioskMenu();
	}

	kioskOrderStart() {
		this.activeOrder = new BehaviorSubject<Order>(
			new Order({
				venueKey: this.venueService.venueKey,
				checkKey: uuidv4(),
				source: CheckSources.Kiosk,
				seats: [
					new OrderSeat({
						items: [],
						total: 0,
					}),
				],
			})
		);

		this.activeOrder.next(this.activeOrder.value);
	}

	kioskOrderAddItem(item: OrderItem) {
		console.log(`Add to order`);
		this.activeOrder.value.seats[0].items.push(item);
		this.activeOrder.next(this.activeOrder.value);
		this.kioskOrderRecalculateTotals();
	}

	kioskOrderRemoveItem(parentIndex: number, modifierIndex?: number) {
		console.log(`Remove from order`);
		modifierIndex && this.activeOrder.value.seats[0].items[parentIndex].modifiers.splice(modifierIndex, 1);
		!modifierIndex && this.activeOrder.value.seats[0].items.splice(parentIndex, 1);
		this.kioskOrderRecalculateTotals();
		this.activeOrder.next(this.activeOrder.value);
	}

	kioskOrderRecalculateTotals() {
		let itemsTotal: number = 0;
		let paymentsTotal: number = 0;
		this.activeOrder.value.seats[0].items.forEach((item) => {
			itemsTotal += item.price * item.quantity;
			item.modifiers.forEach((modifier) => {
				itemsTotal += modifier.price * modifier.quantity;
			});
		});

		this.activeOrder.value.seats[0].payments?.forEach((pymt) => {
			paymentsTotal += pymt.amount;
		});
		this.activeOrder.value.seats[0].subtotal = itemsTotal;
		this.activeOrder.value.seats[0].taxtotal = this.activeOrder.value.seats[0].subtotal * (this.venueService.ooVenue.taxRate / 100);
		this.activeOrder.value.seats[0].total = this.activeOrder.value.seats[0].taxtotal + this.activeOrder.value.seats[0].subtotal;
		this.activeOrder.value.seats[0].remainingBalance = this.activeOrder.value.seats[0].total - paymentsTotal;
		this.activeOrder.value.subtotal = this.activeOrder.value.seats[0].subtotal;
		this.activeOrder.value.taxtotal = this.activeOrder.value.seats[0].taxtotal;
		this.activeOrder.value.total = this.activeOrder.value.seats[0].total;
		this.activeOrder.next(this.activeOrder.value);
	}

	paymentResponseAttachToOrder(paymentResponse: DatacapResponse, tip: number) {
		const payment = new OrderPayment({
			amount: +paymentResponse.TranResponse.Amount.Authorize,
			tip: tip,
			token: paymentResponse.TranResponse.RecordNo,
			type: OrderPaymentTypes.CreditCard,
			tipPercent: undefined,
			displayName: paymentResponse.TranResponse.AcctNo,
			pspReference: paymentResponse.TranResponse.PayAPI_Id,
		});

		this.activeOrder.value.seats[0].payments = [payment];
		this.kioskOrderRecalculateTotals();
		this.activeOrder.next(this.activeOrder.value);
	}

	submitKioskOrder() {
		this.activeOrder.value.dueDateTime = this.app.moment().add(5, 'minutes').toISOString();
		this.focuslink.sendOrder(this.venueService.venueKey, this.activeOrder.value).then((order) => {});
	}
}
